import TwoColumnLayout from "layout/TwoColumnLayout";
import React from "react";
import MainDetailsPanel from "./Internal/MainDetailsPanel";
import moment from "moment";
import { type ShowDates } from "components/showdatepicker/models";
import { useValidators } from "formvalidation/hooks";
import ValidatorContext from "formvalidation/FormContext";
import { useBookkeepingCurrency } from "components/exchangerate/hooks";
import EditViewPanel from "components/panels/EditViewPanel";
import ArtistAttendeePickerSection from "show/components/ArtistAttendeePicker/App";
import CollapsibleSection from "components/DetailPage/CollapsibleSection";
import VenuePanel from "./Internal/VenuePanel";
import PromoterPanel from "./Internal/PromoterPanel";
import HotelPanel from "./Internal/HotelPanel";
import TicketsPanel from "./Internal/TicketsPanel";
import { useFreefieldState } from "components/Freefields/hooks";
import FreefieldPanel from "./FreefieldPanel";
import { Button } from "semantic-ui-react";
import { useMutation } from "@tanstack/react-query";
import { routes } from "utilities/routing/generatedRoutes";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import ItinerarySidebar from "show/ItinerarySidebar/app";
import { ExclusivityPreviewPanel } from "exclusivityclause/ExcusivityPreviewPanel";
import { mapShowInfoToUpdateShowInternalInstruction, ShowInfo } from "./utils";
import { ExclusivityCheckFields } from "exclusivityclause/utils";

interface Props {
  type: "newShow" | { existingShowId: number }
  initialValues: ShowInfo
  exclusivityClause: server.dto.ExclusivityClause
  ffCategories: server.dto.FreeFieldCategorySmall[]
  ffPermissions: server.dto.FreeFieldDefinitionPermission[]
  optionsPanel: server.dto.OptionsPanelDataV2
  listUrl: string
  statuses: server.dto.StatusSmall[]
  itineraryPanel: server.dto.ItinerarySidebarPanelConfig
  hasAccessToAllArtists: boolean
}

const ShowInternalEditApp = ({
  exclusivityClause,
  ffCategories,
  ffPermissions,
  type,
  initialValues,
  optionsPanel,
  listUrl,
  statuses,
  itineraryPanel,
  hasAccessToAllArtists
}: Props) => {
  const [showDates, setShowDates] = React.useState<ShowDates>({
    startDisplayDate: moment.utc(initialValues.showDates.startDisplayDate),
    endDisplayDate: moment.utc(initialValues.showDates.endDisplayDate),
    timeOfPerformance: initialValues.showDates.timeOfPerformance,
    mode: initialValues.showDates.mode
  });
  const [production, setProduction] = React.useState(initialValues.production);

  const [status, setStatus] = React.useState(initialValues.status);
  const [eventName, setEventName] = React.useState(initialValues.eventName);
  const [eventWebsite, setEventWebsite] = React.useState(initialValues.eventWebsite ?? "");

  const [publication, setPublication] = React.useState(initialValues.publication);
  const [costCenter, setCostCenter] = React.useState(initialValues.costCenter);
  const [projectCode, setProjectCode] = React.useState(initialValues.projectCode ?? "");
  const [tags, setTags] = React.useState(initialValues.tags);
  const [owner, setOwner] = React.useState(initialValues.owner);

  const [artist, setArtist] = React.useState<server.dto.ArtistSmall>(initialValues.artist);
  const [attendees, setAttendees] = React.useState(initialValues.attendees);

  const [venue, setVenue] = React.useState(initialValues.venue);
  const [venueContact, setVenueContact] = React.useState(initialValues.venueContact);

  const [capacity, setCapacity] = React.useState(initialValues.capacity);
  const [ageRestriction, setAgeRestriction] = React.useState(initialValues.ageRestriction);
  const [doorsOpen, setDoorsOpen] = React.useState(initialValues.doorsOpen);
  const [doorsClose, setDoorsClose] = React.useState(initialValues.doorsClose);

  const [promoter, setPromoter] = React.useState(initialValues.promoter);
  const [promoterContact, setPromoterContact] = React.useState(initialValues.promoterContact);
  const [hotel, setHotel] = React.useState(initialValues.hotel);
  const [hotelContact, setHotelContact] = React.useState(initialValues.hotelContact);

  const [onSaleDate, setOnSaleDate] = React.useState(initialValues.onSaleDate);
  const [soldOut, setSoldOut] = React.useState(initialValues.soldOut ?? false);
  const [ticketLink, setTicketLink] = React.useState(initialValues.ticketLink ?? "");

  const [presalePrice, setPresalePrice] = React.useState(initialValues.presalePrice);
  const [doorPrice, setDoorPrice] = React.useState(initialValues.doorPrice);
  const [reducedPrice, setReducedPrice] = React.useState(initialValues.reducedPrice);

  const freefieldState = useFreefieldState(initialValues.freefieldValues, ffCategories, ffPermissions);

  const editMutation = useMutation(window.CoreApi.Show.UpdateForInternalEnvironment);
  const createMutation = useMutation(window.CoreApi.Show.InsertForInternalEnvironment);

  const [isGoingtoSubmit, setIsGoingToSubmit] = React.useState(false);

  /**
     * Ugly hack to handle the following case:
     * 1. Focus is in an input field that updates state onBlur
     * 2. User presses enter
     * 3. Form is submitted before the new state is set
     */
  React.useEffect(() => {
    if (isGoingtoSubmit) {
      onSubmit();
      setIsGoingToSubmit(false);
    }
  }, [isGoingtoSubmit]);

  const [stayLoading, setStayLoading] = React.useState(false);

  const onSubmit = async () => {
    if (isSaving) return;

    const res = await validators.validateAll();

    if (res.hasErrors) {
      res.scrollToFirstError();
      return;
    }

    const dto = mapShowInfoToUpdateShowInternalInstruction(
      {
        production,
        showDates,
        status,
        eventName,
        eventWebsite,
        publication,
        costCenter,
        projectCode,
        tags,
        owner,
        artist,
        attendees,
        venue,
        venueContact,
        capacity,
        ageRestriction,
        doorsOpen,
        doorsClose,
        promoter,
        promoterContact,
        hotel,
        hotelContact,
        onSaleDate,
        soldOut,
        ticketLink,
        presalePrice,
        doorPrice,
        reducedPrice,
        bookkeeping: bookkeepingCurrency.bookkeeping,
        currency: bookkeepingCurrency.currency,
        exchangeRate: bookkeepingCurrency.exchangeRate,
        freefieldValues: freefieldState.editableFreefieldValues
      },
      freefieldState.editableFreefieldValues,
      production
    )

    let wasSuccessful: boolean;
    let idToRedirectTo: number;

    if (type === "newShow") {
      const response = await createMutation.mutateAsync({
        ...dto,
        validationStatus: "validated"
      });
      wasSuccessful = !response.data.hasErrors;
      idToRedirectTo = response.data.data;
    } else {
      const response = await editMutation.mutateAsync({
        ...dto,
        showId: type.existingShowId
      });
      wasSuccessful = !response.data.hasErrors;
      idToRedirectTo = type.existingShowId;
    }

    if (wasSuccessful) {
      setStayLoading(true);
      window.location.href = routes.show_Detail(idToRedirectTo).absolute;
    }
  };

  const bookkeepingCurrency = useBookkeepingCurrency(initialValues.bookkeeping, initialValues.currency, initialValues.exchangeRate, {
    useBaseCurrencyOnBookkeepingChange: type === "newShow"
  });

  const onTimeOfPerformanceChange = (newTimeOfPerformance: string) => {
    setShowDates(prevDates => ({
      ...prevDates,
      timeOfPerformance: newTimeOfPerformance
    }));
  };

  const onVenueChange = (
    venue: server.dto.VenueSmall,
    capacity: number,
    ageRestriction: server.dto.AgeRestriction,
    doorsOpen: moment.Moment | null,
    doorsClose: moment.Moment | null
  ) => {
    setVenue(venue);
    setCapacity(capacity);
    setAgeRestriction(ageRestriction);
    setDoorsOpen(doorsOpen);
    setDoorsClose(doorsClose);
  };

  const validators = useValidators();

  const displayArtist = site.currentEnvironment.functionGroups.artist.enabled;
  const displayVenue = site.currentEnvironment.functionGroups.venue.enabled;
  const displayPromoter = site.currentEnvironment.functionGroups.promoter.enabled;
  const displayHotel = site.currentEnvironment.functionGroups.hotel.enabled;

  const exclusivityCheckFields: ExclusivityCheckFields = {
    artistId: artist?.artistId,
    excludeShowId: type !== "newShow" ? type.existingShowId : null,
    exclusivityClause,
    showStartDisplay: showDates.startDisplayDate,
    showEndDisplay: showDates.endDisplayDate,
    venueLocation: venue?.location,
    venueId: venue?.venueId,
    appointmentPhase: status.appointmentPhase,
  }

  const isSaving = editMutation.isLoading || createMutation.isLoading || stayLoading;
  const hasServerError = editMutation.data?.data?.hasErrors || createMutation.data?.data?.hasErrors || editMutation.isError || createMutation.isError;
  return (
    <ValidatorContext settings={validators.validatorSettings}>
      <TwoColumnLayout>
        <TwoColumnLayout.LeftColumn>
          <EditViewPanel title={site.currentEnvironment.show.singular}>
            <form onSubmit={e => {
              e.preventDefault();
              setIsGoingToSubmit(true);
            }} className="flex-column gap-bigger">
              <CollapsibleSection
                uniqueRowId="showEditMainDetials"
                collapsible="no"
                type="form"
              >
                <MainDetailsPanel
                  production={production}
                  onProductionChanged={setProduction}

                  dates={showDates}
                  onDatesChanged={setShowDates}
                  onTimeOfPerformanceChanged={onTimeOfPerformanceChange}

                  status={status}
                  onStatusChanged={setStatus}
                  statuses={statuses}

                  initialEventName={eventName}
                  onEventNameChanged={setEventName}

                  eventWebsite={eventWebsite}
                  onEventWebsiteChanged={setEventWebsite}

                  publication={publication}
                  onPublicationChanged={setPublication}

                  costCenter={costCenter}
                  onCostCenterChanged={setCostCenter}

                  projectCode={projectCode}
                  onProjectCodeChanged={setProjectCode}

                  tags={tags}
                  onTagToggle={setTags}

                  owner={owner}
                  onOwnerChanged={setOwner}

                  bookkeepingCurrency={bookkeepingCurrency}
                />
              </CollapsibleSection>

              {displayArtist && <ArtistAttendeePickerSection
                validatorKey="artist"
                isRequired={!hasAccessToAllArtists}
                artist={artist}
                onArtistChange={setArtist}
                attendees={attendees}
                onAttendeesChange={setAttendees}
                autoSelectDefaultAttendees={type === "newShow"}
              />}

              {displayVenue && <CollapsibleSection
                title={site.currentEnvironment.functionGroups.venue.singular}
                uniqueRowId="showEditVenue"
                collapsible="startOpen"
                type="form">
                <VenuePanel
                  venue={venue}
                  onVenueChange={onVenueChange}
                  onVenueRemoved={() => {
                    setVenue(null);
                    setCapacity(null);
                    setAgeRestriction(null);
                    setDoorsOpen(null);
                    setDoorsClose(null);
                  }}
                  capacity={capacity}
                  onCapacityChange={setCapacity}
                  ageRestriction={ageRestriction}
                  onAgeRestrictionChange={setAgeRestriction}
                  doorsOpen={doorsOpen}
                  onDoorsOpenChange={setDoorsOpen}
                  doorsClose={doorsClose}
                  onDoorsCloseChange={setDoorsClose}
                  contact={venueContact}
                  onContactChange={setVenueContact}
                />
              </CollapsibleSection>}

              {displayPromoter && <CollapsibleSection
                title={site.currentEnvironment.functionGroups.promoter.singular}
                uniqueRowId="showEditPromoter"
                collapsible="startOpen"
                type="form">
                <PromoterPanel
                  company={promoter}
                  onCompanyChange={setPromoter}
                  contact={promoterContact}
                  onContactChange={setPromoterContact}
                />
              </CollapsibleSection>}

              {displayHotel && <CollapsibleSection
                title={site.currentEnvironment.functionGroups.hotel.singular}
                uniqueRowId="showEditHotel"
                collapsible="startOpen"
                type="form">
                <HotelPanel
                  company={hotel}
                  onCompanyChange={setHotel}
                  contact={hotelContact}
                  onContactChange={setHotelContact}
                />
              </CollapsibleSection>}

              <CollapsibleSection
                title={RESX.Ticket.Tickets}
                uniqueRowId="showEditTickets"
                collapsible="startOpen"
                type="form">
                <TicketsPanel
                  onSaleDate={onSaleDate}
                  onSaleDateChange={setOnSaleDate}

                  soldOut={soldOut}
                  onSoldOutChange={setSoldOut}

                  doorPrice={doorPrice}
                  onDoorPriceChange={setDoorPrice}

                  presalePrice={presalePrice}
                  onPresalePriceChange={setPresalePrice}

                  reducedPrice={reducedPrice}
                  onReducedPriceChange={setReducedPrice}

                  ticketLink={ticketLink}
                  onTicketLinkChange={setTicketLink}

                  ticketCurrency={bookkeepingCurrency.currency}
                />
              </CollapsibleSection>

              {freefieldState
                .freefieldCategoriesWithValuesAndPermissions
                // If the category only has view only fields with no visible values, don't show the category
                .filter(cat => cat.definitionWithValues.some(def => def.permission !== "view" || def.displayValue.hasVisibleValue))
                .map(cat => (
                  <CollapsibleSection
                    key={cat.freeFieldCategoryId}
                    title={cat.title}
                    uniqueRowId={`showEditFreefieldCategory${cat.freeFieldCategoryId}`}
                    collapsible="startOpen"
                    type="form">
                    <FreefieldPanel freefieldCategoryWithValueAndPermission={cat} freefieldState={freefieldState} />
                  </CollapsibleSection>
                ))}

              <div className="flex">
                <div className="grow1 red">{hasServerError ? RESX.GeneralLabels.SomethingUnexpectedHappened : ""}</div>
                <div>
                  <Button type="submit" disabled={isSaving} loading={isSaving} primary>{RESX.Buttons.btnSave}</Button>
                </div>
              </div>
            </form>
          </EditViewPanel>
        </TwoColumnLayout.LeftColumn>

        <TwoColumnLayout.RightColumn>
          <GeneralOptionsPanel
            data={optionsPanel}
            links={[
              {
                text: RESX.Buttons.ListView,
                href: listUrl,
                visible: true
              },
              {
                text: RESX.Buttons.DetailView,
                href: routes.show_Detail(type === "newShow" ? 0 : type.existingShowId).absolute,
                visible: type !== "newShow"
              }
            ]}
          />

          {(artist != null && itineraryPanel.displayPanel) && <ItinerarySidebar
            showId={type === "newShow" ? undefined : type.existingShowId}
            artist={artist}
            showStartDate={showDates.startDisplayDate}
            showEndDate={showDates.endDisplayDate}
            panelConfig={itineraryPanel}
            disableNewAppointments
            canLinkUnlinkTravel={false}
          />}

          <ExclusivityPreviewPanel exclusivityCheckFields={exclusivityCheckFields} />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout>
    </ValidatorContext>
  );
};

export default ShowInternalEditApp;